import { useState } from "react";
import { ChevronDown, CircleUserRound, ExternalLink, Globe, LogOut, Monitor, Moon, PenLine, Settings2, Shield, Sun } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Button, LocaleFlag, cn,
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@wa/ui";
import { useI18n, useT } from "../../i18n/I18nProvider";
import { ACCOUNT_URL, ADMIN_URL, userCanPlatform, type StaffUser } from "../../lib/auth";
import { applyThemeMode, readThemeMode, type ThemeMode } from "../../lib/themeMode";
import { Avatar } from "../ui/Avatar";
import { MyMailDialog } from "../sections/email/signatures";
import { PasskeyDialog } from "./PasskeyDialog";
import { useClosePops } from "./Pop";
import { Seg } from "./Seg";

const THEMES: { value: ThemeMode; icon: typeof Sun; titleKey: string }[] = [
  { value: "system", icon: Monitor, titleKey: "theme.system" },
  { value: "light", icon: Sun, titleKey: "theme.light" },
  { value: "dark", icon: Moon, titleKey: "theme.dark" },
];

const LOCALES = [
  { code: "et", label: "Eesti" },
  { code: "en", label: "English" },
] as const;

// The panel and its rows in the CRM's own look (`.crm-menu`, `.crm-mi` and
// `.crm-menu-sep` in index.css). Those rules sit in `@layer components`, which
// the kit's utilities outrank, so the look is restated here as utilities
// rather than borrowed by class name.
const PANEL = "min-w-[232px] rounded-[20px] border-0 bg-(--crm-surface) p-[7px] shadow-(--crm-sh-lg)";
const ROW = "cursor-pointer gap-2.5 rounded-full px-3 py-[7px] text-[12.75px] font-[550] focus:bg-(--crm-sunk) focus:text-foreground";
const ICON = "size-4 shrink-0 text-muted-foreground";
const SEP = "mx-[9px] my-[5px] bg-(--crm-hair)";

/**
 * The account dropdown - and the home for the two settings that used to be
 * separate header buttons.
 *
 * A kit DropdownMenu, so Radix owns the keyboard, Escape, focus return and
 * outside clicks, and every row is a menu item the arrow keys reach. It sits
 * on the kit's menu layer (z 120), above the CRM's own dropdowns (97).
 *
 * Theme and language are switches, not destinations: picking one keeps the
 * menu open (`preventDefault` in `onSelect`). Putting them here buys back two
 * slots in a header row that also has to carry search, mail and
 * notifications, and it groups "things about me" in the one place named after
 * me.
 *
 * The language is a switch in the panel and not a submenu on purpose. The chip
 * sits at the right edge of the header, so a submenu flips to the left, and on
 * a phone there is no room there either: Radix places it off the screen and
 * cannot move it back sideways.
 */
export function AccountMenu({ user, onLogout }: { user: StaffUser; onLogout: () => void }) {
  const t = useT();
  const { locale, setLocale } = useI18n();
  const lang = locale.split("-")[0] ?? "et";
  const [theme, setTheme] = useState<ThemeMode>(() => readThemeMode());
  // Passkey enrolment lives here rather than in the account hub, because a key
  // only works on the domain it was made for and the hub is a different one.
  const [passkeyOpen, setPasskeyOpen] = useState(false);
  // The person's own signatures and default sender: "things about me", so
  // here, and only for someone who may write letters at all.
  const [mineOpen, setMineOpen] = useState(false);
  const canMail = userCanPlatform(user, "manage:crm.email");
  const closePops = useClosePops();

  return (
    <>
      <DropdownMenu
        // Opening this menu closes the CRM's own dropdowns (mail, alerts, the
        // timer). Radix opens on pointerdown and cancels the mousedown after
        // it, which is the event `PopHost` closes them on, so without this the
        // mail menu stayed up beside this one.
        onOpenChange={(open) => {
          if (open) closePops();
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              // The account chip. Below 1024px it is only its avatar:
              // crm-topbar.css hides the label and the caret, and `max-lg`
              // drops the right padding that made room for them.
              "h-11 gap-2.5 rounded-full bg-(--crm-surface) py-[3px] pl-[3px] pr-4 font-normal shadow-(--crm-sh-sm) transition-shadow active:scale-100 max-lg:p-[3px]",
              "hover:bg-(--crm-surface) hover:text-foreground hover:shadow-(--crm-sh-md) data-[state=open]:shadow-(--crm-sh-md)",
            )}
          >
            <Avatar name={user.name || user.email} tone="brand" />
            {/* Hidden below 1024px by crm-topbar.css, which owns how the top bar
                gives up room; the name is the first line of the menu. */}
            <span className="crm-me-label text-left">
              <span className="crm-me-n block text-[13.25px] font-[650] leading-[1.15]">{user.name || user.email}</span>
              <span className="mt-px block text-[11px] text-muted-foreground">{t("app.badge")}</span>
            </span>
            <ChevronDown className="crm-me-caret size-3! text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          sideOffset={12}
          collisionPadding={10}
          className={cn(PANEL, "max-h-(--radix-dropdown-menu-content-available-height) overflow-y-auto")}
        >
          <div className="crm-menu-head">
            <div className="crm-menu-t">{user.name || user.email}</div>
            <div className="crm-menu-s">{user.email}</div>
          </div>

          <DropdownMenuItem asChild className={ROW}>
            <a href={ADMIN_URL}>
              <ExternalLink className={ICON} />
              {t("app.openCms")}
            </a>
          </DropdownMenuItem>
          {/* The account hub is a sibling app, so it sits next to the admin center
              rather than next to the CRM's own settings below. */}
          <DropdownMenuItem asChild className={ROW}>
            <a href={ACCOUNT_URL}>
              <CircleUserRound className={ICON} />
              {t("app.openAccount")}
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className={ROW}>
            <Link to="/settings">
              <Settings2 className={ICON} />
              {t("nav.settings")}
            </Link>
          </DropdownMenuItem>
          {/* Named "Security" with the shield, like the same row in the account
              hub. There it opens the whole security screen; here the dialog holds
              only passkeys, because they are the one part bound to this domain. */}
          <DropdownMenuItem className={ROW} onSelect={() => setPasskeyOpen(true)}>
            <Shield className={ICON} />
            {t("passkey.menu")}
          </DropdownMenuItem>
          {canMail ? (
            <DropdownMenuItem className={ROW} onSelect={() => setMineOpen(true)}>
              <PenLine className={ICON} />
              {t("myMail.menu")}
            </DropdownMenuItem>
          ) : null}

          <DropdownMenuSeparator className={SEP} />

          <div className="crm-mrow">
            <span className="crm-mrow-l">
              <Moon className="size-4" />
              {t("theme.label")}
            </span>
            <Seg
              label={t("theme.label")}
              value={theme}
              onChange={(v) => {
                applyThemeMode(v);
                setTheme(v);
              }}
              options={THEMES.map((m) => {
                const Icon = m.icon;
                return { value: m.value, title: t(m.titleKey), label: <Icon className="size-3" /> };
              })}
            />
          </div>

          <div className="crm-mrow">
            <span className="crm-mrow-l">
              <Globe className="size-4" />
              {t("common.switchLanguage")}
            </span>
            <Seg
              label={t("common.switchLanguage")}
              value={lang}
              onChange={setLocale}
              options={LOCALES.map((l) => ({
                value: l.code,
                title: l.label,
                label: (
                  <>
                    <LocaleFlag locale={l.code} className="crm-seg-flag" />
                    {l.code.toUpperCase()}
                  </>
                ),
              }))}
            />
          </div>

          <DropdownMenuSeparator className={SEP} />

          <DropdownMenuItem
            destructive
            onSelect={onLogout}
            className={cn(ROW, "text-destructive focus:bg-destructive/10 focus:text-destructive")}
          >
            <LogOut className="size-4 shrink-0" />
            {t("app.logout")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {/* Outside the menu: the panel unmounts on close, the dialog must not. */}
      {passkeyOpen && <PasskeyDialog onClose={() => setPasskeyOpen(false)} />}
      {mineOpen && <MyMailDialog onClose={() => setMineOpen(false)} />}
    </>
  );
}
